import { defineStore } from "pinia"
import { indicadorService } from "@/services/indicadorDeBemEstar.service"
import { computed, reactive, ref } from "vue"
import type { IndicatorType, LoaderType } from "@/types/types"
import { newDialog, toast, i18N } from "@/common/util"

export const useIndicadorDeBemEstarStore = defineStore('indicadorDeBemEstar', () => {
  /**
   * States
   */
  const list = ref<IndicatorType[]>([])
  const status = reactive<Record<string, LoaderType>>({
    getAll: 'initial',
    create: 'initial',
    update: 'initial',
    delete: 'initial',
    getById: 'initial',
  })

  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  /**
   * Actions
   */
  const getAll = async (): Promise<void> => {
    status.getAll = 'loading'

    try {
      const { data } = await indicadorService.get<IndicatorType[]>()
      list.value = data
      status.getAll = 'success'
    } catch {
      status.getAll = 'error'
    }
  }

  const getById = async (id: string) => {
    status.getById = 'loading'
    try {
      const { data, success } = await indicadorService.get<IndicatorType>(id)
      if (success) {
        status.getById = 'success'
        return data
      }
    } catch {
      status.getById = 'error'
    }
  }

  const create = async (objData: IndicatorType) => {
    status.create = 'loading'
    try {
      const { data, success } = await indicadorService.post(null, objData)
      
      if (success) {
        toast.success({message: i18N('toasts.createSuccess')})
        getAll()
      }

      status.create = 'success'
      return data
    } catch {
      status.create = 'error'
    }
  }

  const update = async (objData: IndicatorType) => {
    status.update = 'loading'

    try {
      const { data, success } = await indicadorService.put(objData.id, objData)
      
      if (success) {
        toast.success({message: i18N('toasts.updateSuccess')})
        getAll()
      }

      status.update = 'success'
      return data
    } catch {
      status.update = 'error'
    }
  }

  const remove = async (data: IndicatorType, force?: boolean) => {
    if(!force) 
      return newDialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.wellBeingIndicator').toLowerCase(), name: data.nome}),
        complete: () => remove(data, true)
      })

    try {
      status.delete = 'loading'
      const { success } = await indicadorService.delete(data.id)

      if (success) {
        getAll()
        toast.success({ message: i18N('toasts.removeSuccess') })
      }
      status.delete = 'success'
      return true
    } catch {
      status.delete = 'success'
    }
  }

  return {
    list,
    status,
    loading,
    getAll,
    getById,
    create,
    update,
    remove
  }
})